import useAutomationValues from './useAutomationValues';

import {
  filterCustomAttributes,
  getCustomAttributeInputType,
  getStandardAttributeInputType,
  isCustomAttribute,
} from 'dashboard/helper/automationHelper';

export function useEditableAutomation() {
  const { getConditionDropdownValues, getActionDropdownValues } =
    useAutomationValues();

  /**
   * This function sets the conditions for automation.
   * It help to format the conditions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing conditions to manifest.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @returns {Array} An array of manifested conditions.
   */
  const manifestConditions = (
    automation,
    allCustomAttributes,
    automationTypes
  ) => {
    const customAttributes = filterCustomAttributes(allCustomAttributes);
    return automation.conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );
      let inputType = 'plain_text';
      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          automation.event_name,
          condition.attribute_key
        );
      }
      const base = {
        ...condition,
        query_operator: condition.query_operator || 'and',
      };

      if (inputType === 'plain_text' || inputType === 'date') {
        let val = condition.values[0];
        if (condition.attribute_key === 'rating') {
          val = parseInt(val, 10);
        }
        return { ...base, values: val };
      }
      if (inputType === 'comma_separated_plain_text') {
        return { ...base, values: condition.values.join(',') };
      }
      const dropdownValues = getConditionDropdownValues(
        condition.attribute_key
      );
      const hasBooleanOptions =
        inputType === 'search_select' &&
        dropdownValues.length &&
        dropdownValues.every(item => typeof item.id === 'boolean');

      if (hasBooleanOptions) {
        return {
          ...base,
          values: dropdownValues.find(item => item.id === condition.values[0]),
        };
      }
      return {
        ...base,
        values: [...dropdownValues].filter(item =>
          [...(condition.values || [])].includes(item.id)
        ),
      };
    });
  };

  /**
   * Generates an array of actions for the automation.
   * @param {Object} action - The action object.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array|Object} Generated actions array or object based on input type.
   */
  const generateActionsArray = (action, automationActionTypes) => {
    const params = action.action_params || [];
    const actionType = automationActionTypes.find(
      item => item.key === action.action_name
    );
    const inputType = actionType?.inputType;
    if (inputType === 'multi_select' || inputType === 'search_select') {
      return [...(getActionDropdownValues(action.action_name) || [])].filter(
        item => [...params].includes(item.id)
      );
    }
    if (inputType === 'team_message' && params[0]) {
      return {
        team_ids: [
          ...(getActionDropdownValues(action.action_name) || []),
        ].filter(item => [...(params[0].team_ids || [])].includes(item.id)),
        message: params[0].message,
      };
    }
    if (action.action_name === 'send_alert') {
      return {
        inbox_id: params[0]?.inbox_id || null,
        template_id: params[0]?.template_id || null,
        phone_number: params[0]?.phone_number || '',
      };
    }
    return [...params];
  };

  /**
   * This function sets the actions for automation.
   * It help to format the actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing actions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array} An array of manifested actions.
   */
  const manifestActions = (automation, automationActionTypes) => {
    return automation.actions.map(action => ({
      ...action,
      action_params: action.action_params.length
        ? generateActionsArray(action, automationActionTypes)
        : [],
    }));
  };

  /**
   * Formats the automation object for use when we edit the automation.
   * It help to format the conditions and actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object to format.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Object} A new object with formatted automation data, including automation conditions and actions.
   */
  const formatAutomation = (
    automation,
    allCustomAttributes,
    automationTypes,
    automationActionTypes
  ) => {
    return {
      ...automation,
      conditions: manifestConditions(
        automation,
        allCustomAttributes,
        automationTypes
      ),
      actions: manifestActions(automation, automationActionTypes),
    };
  };

  return { formatAutomation };
}
